import axios from 'axios';
import logger from '../utils/logger';

// Create axios instance with base configuration
// Try to detect the backend URL automatically
const getBaseURL = () => {
  // Check for environment variable first (production) - this is the most reliable
  if (process.env.REACT_APP_API_URL) {
    logger.debug('Using API URL from environment:', process.env.REACT_APP_API_URL);
    return process.env.REACT_APP_API_URL;
  }
  
  const hostname = window.location.hostname;
  const protocol = window.location.protocol; // http: or https:
  
  // If accessing from localhost, use localhost for backend
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    logger.debug('Using localhost API URL');
    return 'http://localhost:5000/api';
  }
  
  // If accessing from Vercel (vercel.app domain), warn about missing env var
  // You MUST set REACT_APP_API_URL in Vercel environment variables
  if (hostname.includes('vercel.app')) {
    logger.error('❌ CRITICAL: REACT_APP_API_URL not set in Vercel!');
    logger.error('Please add it in Vercel Dashboard → Your Project → Settings → Environment Variables');
    logger.error('Value should be your Railway backend URL (e.g., https://your-app.railway.app/api)');
    // Don't throw - instead return a clear error URL that will fail gracefully
    return '/api'; // This will fail, but at least the app won't crash immediately
  }
  
  // If accessing from production domain (batinosgarden.shop), use API subdomain
  if (hostname === 'batinosgarden.shop' || hostname === 'www.batinosgarden.shop') {
    return `${protocol}//api.batinosgarden.shop/api`;
  }
  
  // If accessing from network IP, use the same IP for backend
  // This allows laptop/phone access to work automatically
  return `http://${hostname}:5000/api`;
};

const API = axios.create({
  baseURL: getBaseURL(),
  timeout: 10000, // 10 seconds - faster failure, rely on cache for retries
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // send cookies
});

// Create a separate instance for delivery quotes with longer timeout
const DeliveryAPI = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000, // 30 seconds for geocoding and calculation
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add Authorization header to DeliveryAPI
DeliveryAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for DeliveryAPI
DeliveryAPI.interceptors.response.use(
  (response) => response.data,
  (error) => Promise.reject(error.response?.data || { 
    message: error.message || 'Network error',
    code: error.code,
    details: 'Backend server may not be running. Please check if the server is started.'
  })
);

// Add Authorization header from localStorage for development and cross-site use
API.interceptors.request.use(
  (config) => {
    // Prefer accessToken, but fall back to legacy token key so older code still works
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for handling errors
API.interceptors.response.use(
  (response) => {
    logger.apiResponse(response.config?.method, response.config?.url, response.data, response.status);
    return response.data;
  },
  (error) => {
    // Enhanced error logging
    const errorDetails = {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      code: error.code,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        baseURL: error.config?.baseURL,
        data: error.config?.data
      }
    };
    
    logger.error('API Error Details:', errorDetails);
    
    // Check if it's a network error (backend not running)
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      logger.error('⚠️ Network Error: Backend server may not be running or not accessible');
      logger.error(`   Attempted URL: ${error.config?.baseURL}${error.config?.url}`);
      logger.error('   Please ensure the backend server is running on port 5000');
    }
    
    // Extract rate limit information from headers
    let errorResponse = error.response?.data || { 
      message: error.message || 'Network error',
      code: error.code,
      details: 'Backend server may not be running. Please check if the server is started.'
    };
    
    // If it's a rate limit error (429), extract remaining time from headers or response data
    if (error.response?.status === 429) {
      // First check if remainingTime is already in the response data (from backend)
      if (errorResponse.remainingTime !== undefined) {
        // Already set by backend
      } else {
        // Fallback: try to extract from headers
        const resetTime = error.response.headers['ratelimit-reset'] || error.response.headers['RateLimit-Reset'];
        if (resetTime) {
          // resetTime is Unix timestamp in seconds
          const resetTimestamp = parseInt(resetTime);
          const now = Math.floor(Date.now() / 1000); // Current time in seconds
          const remainingTime = Math.max(0, resetTimestamp - now);
          errorResponse = {
            ...errorResponse,
            remainingTime,
            code: errorResponse.code || 'RATE_LIMIT_EXCEEDED'
          };
        }
      }
    }

    // Global handler for forced logout when session is revoked on another device
    if (error.response?.status === 401 && errorResponse.code === 'SESSION_REVOKED') {
      try {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        sessionStorage.clear();
      } catch (storageError) {
        // Ignore storage errors
      }

      // Notify React tree to show a confirmation modal
      window.dispatchEvent(new Event('forced-logout'));
    }
    
    return Promise.reject(errorResponse);
  }
);

// Product API endpoints
export const productAPI = {
  // Get all products with filtering
  getProducts: (params = {}) => {
    return API.get('/products', { params });
  },

  // Get single product by ID
  getProduct: (id) => {
    return API.get(`/products/${id}`);
  },

  // Get featured products
  getFeaturedProducts: () => {
    return API.get('/products/featured');
  },

  // Get top selling products
  getTopSellingProducts: () => {
    return API.get('/products/top-selling');
  },

  // Get imported products
  getImportedProducts: () => {
    return API.get('/products/imported');
  },

  // Get all categories
  getCategories: () => {
    return API.get('/products/categories');
  },
  // Get all characteristics
  getCharacteristics: () => {
    return API.get('/products/characteristics');
  },

  // Search products
  searchProducts: (query, params = {}) => {
    return API.get('/products', { 
      params: { search: query, ...params } 
    });
  },

  // Admin endpoints
  getAllProductsAdmin: (params = {}) => {
    return API.get('/products/admin/all', { params });
  },

  createProduct: (productData) => {
    // Use longer timeout for product creation (image uploads can take time)
    return API.post('/products', productData, {
      timeout: 60000 // 60 seconds for image uploads to Cloudinary
    });
  },

  updateProduct: (id, productData) => {
    // Use longer timeout for product updates (image uploads can take time)
    return API.put(`/products/${id}`, productData, {
      timeout: 60000 // 60 seconds for image uploads to Cloudinary
    });
  },

  deleteProduct: (id) => {
    return API.delete(`/products/${id}`);
  }
};

// Bundle API endpoints
export const bundleAPI = {
  // Get all bundles
  getBundles: (params = {}) => {
    return API.get('/bundles', { params });
  },

  // Get single bundle by ID
  getBundle: (id) => {
    return API.get(`/bundles/${id}`);
  },

  // Admin endpoints
  getAllBundlesAdmin: () => {
    return API.get('/bundles/admin/all');
  },

  createBundle: (bundleData) => {
    return API.post('/bundles', bundleData, {
      timeout: 60000 // 60 seconds for image uploads
    });
  },

  updateBundle: (id, bundleData) => {
    return API.put(`/bundles/${id}`, bundleData, {
      timeout: 60000
    });
  },

  deleteBundle: (id) => {
    return API.delete(`/bundles/${id}`);
  }
};

// Image Gallery API endpoints
export const imageGalleryAPI = {
  // Get all images from gallery (paginated)
  getImages: (params = {}) => {
    return API.get('/image-gallery', { params });
  },

  // Bulk upload images
  uploadImages: (images) => {
    return API.post('/image-gallery/upload', { images }, {
      timeout: 300000, // 300 seconds (5 minutes) for bulk uploads
      maxContentLength: Infinity,
      maxBodyLength: Infinity
    });
  },

  // Delete image from gallery
  deleteImage: (id) => {
    return API.delete(`/image-gallery/${id}`);
  },

  // Check if image URL is duplicate
  checkDuplicate: (url) => {
    return API.get('/image-gallery/check-duplicate', { params: { url } });
  },

  // Increment usage count
  incrementUsage: (id) => {
    return API.put(`/image-gallery/${id}/usage`);
  }
};

// Variation API endpoints
export const variationAPI = {
  // Get all variations for a product
  getProductVariations: (productId) => {
    return API.get(`/variations/product/${productId}`);
  },

  // Get single variation by ID
  getVariation: (id) => {
    return API.get(`/variations/${id}`);
  },

  // Create new variation (Admin only)
  createVariation: (variationData) => {
    return API.post('/variations', variationData, {
      timeout: 60000 // 60 seconds for image uploads
    });
  },

  // Update variation (Admin only)
  updateVariation: (id, variationData) => {
    return API.put(`/variations/${id}`, variationData, {
      timeout: 60000 // 60 seconds for image uploads
    });
  },

  // Delete variation (Admin only)
  deleteVariation: (id) => {
    return API.delete(`/variations/${id}`);
  }
};

// Analytics API endpoints
export const analyticsAPI = {
  // Get dashboard overview
  getOverview: (timeframe = 30) => {
    return API.get('/analytics/overview', { params: { timeframe } });
  },

  // Get sales analytics
  getSales: (timeframe = 30, groupBy = 'day') => {
    return API.get('/analytics/sales', { params: { timeframe, groupBy } });
  },

  // Get product analytics
  getProducts: (timeframe = 30) => {
    return API.get('/analytics/products', { params: { timeframe } });
  },

  // Get customer analytics
  getCustomers: (timeframe = 30) => {
    return API.get('/analytics/customers', { params: { timeframe } });
  },

  // Get weekly plants sold
  getWeeklyPlants: (timeframe = 7) => {
    return API.get('/analytics/weekly-plants', { params: { timeframe } });
  },

  // Get inventory status
  getInventoryStatus: () => {
    return API.get('/analytics/inventory-status');
  }
};

// Category API endpoints
export const categoryAPI = {
  // Get all categories
  getCategories: () => {
    return API.get('/categories');
  },

  // Create new category
  createCategory: (categoryData) => {
    return API.post('/categories', categoryData);
  },

  // Update category
  updateCategory: (categoryName, categoryData) => {
    return API.put(`/categories/${encodeURIComponent(categoryName)}`, categoryData);
  },

  // Delete category
  deleteCategory: (categoryName) => {
    return API.delete(`/categories/${encodeURIComponent(categoryName)}`);
  }
};

// Stock Adjustment API (for inventory quick adjust & detailed modal)
export const stockAdjustmentAPI = {
  // Create a stock adjustment entry and update product stock
  // Backend endpoint should handle recording audit logs and stock movement
  createAdjustment: (adjustmentData) => {
    return API.post('/stock-adjustments', adjustmentData);
  }
};

// Audit Logs API
export const auditAPI = {
  getAuditLogs: (params = {}) => {
    return API.get('/audit-logs', { params });
  },
  getEntityLogs: (entityType, entityId, params = {}) => {
    return API.get(`/audit-logs/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`, {
      params
    });
  }
};

// Backwards-compatible alias for legacy code
export const auditLogAPI = auditAPI;

// Auth API endpoints
export const authAPI = {
  // Customer authentication
  login: (credentials) => {
    return API.post('/auth/login', credentials);
  },

  register: (userData) => {
    return API.post('/auth/register', userData);
  },

  // Admin authentication
  adminLogin: (credentials) => {
    return API.post('/auth/admin-login', credentials);
  },

  // Profile management
  getProfile: () => {
    return API.get('/auth/profile');
  },

  updateProfile: (profileData) => {
    return API.put('/auth/profile', profileData);
  },

  // Daily Login
  getDailyLoginStatus: () => {
    return API.get('/auth/daily-login');
  },

  claimDailyLoginReward: () => {
    return API.post('/auth/daily-login/claim-reward');
  },

  claimReferralReward: () => {
    return API.post('/auth/claim-referral-reward');
  },

  getReferralHistory: () => {
    return API.get('/auth/referral-history');
  },

  checkReferralReward: () => {
    return API.get('/auth/check-referral-reward');
  },

  // Password management
  changePassword: (passwordData) => {
    return API.post('/auth/change-password', passwordData);
  },

  // Token management
  refreshToken: () => {
    // Cookies will be sent automatically
    return API.post('/auth/refresh-token');
  },

  logout: () => {
    return API.post('/auth/logout');
  },

  // Admin endpoints
  getUsers: () => {
    return API.get('/auth/users');
  },
  clearAllReferrals: () => {
    return API.post('/auth/admin/clear-all-referrals');
  },

  // Password reset
  forgotPassword: (data) => {
    return API.post('/auth/forgot-password', data);
  },

  resetPassword: (data) => {
    return API.post('/auth/reset-password', data);
  }
};

// Voucher API endpoints
export const voucherAPI = {
  getVouchers: () => {
    return API.get('/vouchers');
  },

  getAllVouchersAdmin: () => {
    return API.get('/vouchers/admin/all');
  },

  getVoucher: (voucherId) => {
    return API.get(`/vouchers/${voucherId}`);
  },

  createVoucher: (voucherData) => {
    return API.post('/vouchers', voucherData);
  },

  updateVoucher: (voucherId, voucherData) => {
    return API.put(`/vouchers/${voucherId}`, voucherData);
  },

  deleteVoucher: (voucherId) => {
    return API.delete(`/vouchers/${voucherId}`);
  },

  validateVoucher: (code, orderAmount, isFirstTimeCustomer) => {
    return API.get(`/vouchers/validate/${code}`, {
      params: {
        orderAmount,
        isFirstTimeCustomer
      }
    });
  },

  redeemVoucher: (code) => {
    return API.post(`/vouchers/redeem/${code}`);
  },

  clearAllVouchers: () => {
    return API.post('/vouchers/admin/clear-all-vouchers');
  }
};

// Order API endpoints
export const orderAPI = {
  createOrder: (orderData) => {
    return API.post('/orders', orderData);
  },

  getUserOrders: (page = 1, limit = 10) => {
    return API.get(`/orders?page=${page}&limit=${limit}`);
  },

  getOrder: (orderId) => {
    return API.get(`/orders/${orderId}`);
  },

  cancelOrder: (orderId, reason) => {
    return API.patch(`/orders/${orderId}/cancel`, { reason });
  },

  // Admin endpoints
  getAllOrders: (page = 1, limit = 20, status = undefined) => {
    const statusParam = status ? `&status=${encodeURIComponent(status)}` : '';
    return API.get(`/orders/admin/all?page=${page}&limit=${limit}${statusParam}`);
  },

  updateOrderStatus: (orderId, status, updateData = null) => {
    // If updateData is provided, use it; otherwise just send status
    const payload = updateData || { status };
    return API.patch(`/orders/admin/${orderId}/status`, payload);
  }
};

// Admin API endpoints
export const adminAPI = {
  getDeliveryPricing: () => {
    return API.get('/admin/delivery-pricing');
  },
  
  updateDeliveryPricing: (pricing) => {
    return API.put('/admin/delivery-pricing', { pricing });
  },

  // Get all customers (admin only)
  getCustomers: () => {
    return API.get('/admin/customers');
  },

  // Create new admin account (superadmin only)
  createAdmin: (adminData) => {
    return API.post('/admin/create-admin', adminData);
  },

  // Delete account (superadmin only)
  deleteAccount: (userId) => {
    return API.delete(`/admin/accounts/${userId}`);
  }
};

// Cart API endpoints (for future use)
export const cartAPI = {
  getCart: () => {
    return API.get('/cart');
  },

  addToCart: (productId, quantity = 1) => {
    return API.post('/cart/add', { productId, quantity });
  },

  updateCartItem: (itemId, quantity) => {
    return API.put(`/cart/${itemId}`, { quantity });
  },

  removeFromCart: (itemId) => {
    return API.delete(`/cart/${itemId}`);
  },

  clearCart: () => {
    return API.delete('/cart');
  }
};

// Payment API endpoints (PayMongo)
export const paymentAPI = {
  // Create payment intent
  createPaymentIntent: (paymentData) => {
    return API.post('/payments/create-intent', paymentData);
  },

  // Attach payment method to payment intent
  attachPaymentMethod: (paymentIntentId, paymentMethodId) => {
    return API.post('/payments/attach', { paymentIntentId, paymentMethodId });
  },

  // Get payment intent status
  getPaymentStatus: (paymentIntentId) => {
    return API.get(`/payments/status/${paymentIntentId}`);
  },

  // Get PayMongo public key
  getPublicKey: () => {
    return API.get('/payments/public-key');
  },

  // Create payment link
  createPaymentLink: (linkData) => {
    return API.post('/payments/create-link', linkData);
  },

  // Check and update order payment status manually
  checkOrderStatus: (orderId, linkId) => {
    return API.post('/payments/check-order-status', { orderId, linkId });
  }
};

// Hero Slides API endpoints
export const heroSlideAPI = {
  // Get all hero slides (with longer timeout for production)
  getSlides: (activeOnly = false) => {
    return API.get('/hero-slides', { 
      params: { activeOnly },
      timeout: 20000 // 20 seconds for hero slides
    });
  },

  // Get active hero slides only
  getActiveSlides: () => {
    return API.get('/hero-slides/active');
  },

  // Get single hero slide by ID
  getSlide: (id) => {
    return API.get(`/hero-slides/${id}`);
  },

  // Admin endpoints
  createSlide: (slideData) => {
    return API.post('/hero-slides', slideData);
  },

  updateSlide: (id, slideData) => {
    return API.put(`/hero-slides/${id}`, slideData);
  },

  deleteSlide: (id) => {
    return API.delete(`/hero-slides/${id}`);
  },

  reorderSlides: (slideOrders) => {
    return API.post('/hero-slides/reorder', { slideOrders });
  }
};

// Notifications API endpoints
export const notificationAPI = {
  // Get all notifications
  getNotifications: (read = null, limit = 50) => {
    const params = { limit };
    if (read !== null) {
      params.read = read;
    }
    return API.get('/notifications', { params });
  },

  // Get unread count
  getUnreadCount: () => {
    return API.get('/notifications/unread-count');
  },

  // Mark notification as read
  markAsRead: (notificationId) => {
    return API.put(`/notifications/${notificationId}/read`);
  },

  // Mark all notifications as read
  markAllAsRead: () => {
    return API.put('/notifications/read-all');
  },

  // Delete notification
  deleteNotification: (notificationId) => {
    return API.delete(`/notifications/${notificationId}`);
  },

  // Broadcast notification to all customers (Admin only)
  broadcastNotification: (notificationData) => {
    return API.post('/notifications/admin/broadcast', notificationData);
  },
  // Get admin broadcast notification history (Admin only)
  getBroadcastHistory: (page = 1, limit = 10) => {
    return API.get('/notifications/admin/broadcast-history', {
      params: { page, limit }
    });
  }
};

// Address API endpoints
export const reviewAPI = {
  // Get reviews for a product
  getProductReviews: (productId, params = {}) => {
    return API.get(`/reviews/product/${productId}`, { params });
  },
  
  // Get user's review for a product
  getUserReview: (productId) => {
    return API.get(`/reviews/product/${productId}/user`);
  },
  
  // Create a review
  createReview: (reviewData) => {
    return API.post('/reviews', reviewData);
  },
  
  // Update a review
  updateReview: (reviewId, reviewData) => {
    return API.put(`/reviews/${reviewId}`, reviewData);
  },
  
  // Delete a review
  deleteReview: (reviewId) => {
    return API.delete(`/reviews/${reviewId}`);
  },
  
  // Mark review as helpful
  markHelpful: (reviewId) => {
    return API.post(`/reviews/${reviewId}/helpful`);
  },
  
  // Get user's purchased products (to check if they can review)
  getPurchasedProducts: () => {
    return API.get('/reviews/user/purchased-products');
  },

  // ==================== ADMIN ENDPOINTS ====================
  
  // Get all reviews for admin management
  getAdminReviews: (params = {}) => {
    return API.get('/reviews/admin/all', { params });
  },

  // Toggle highlight status
  toggleHighlight: (reviewId) => {
    return API.patch(`/reviews/admin/${reviewId}/highlight`);
  },

  // Toggle hide status
  toggleHide: (reviewId) => {
    return API.patch(`/reviews/admin/${reviewId}/hide`);
  },

  // Admin delete review
  adminDeleteReview: (reviewId) => {
    return API.delete(`/reviews/admin/${reviewId}`);
  },

  // Get highlighted reviews for landing page
  getHighlightedReviews: (limit = 6) => {
    return API.get('/reviews/highlighted', { params: { limit } });
  }
};

// Shop Reviews API (testimonials from homepage)
export const shopReviewAPI = {
  // Submit a shop review (public)
  submitReview: (reviewData) => {
    return API.post('/shop-reviews', reviewData);
  },

  // Get highlighted shop reviews for landing page
  getHighlightedReviews: (limit = 6) => {
    return API.get('/shop-reviews/highlighted', { params: { limit } });
  },

  // ==================== ADMIN ENDPOINTS ====================
  
  // Get all shop reviews for admin
  getAdminReviews: (params = {}) => {
    return API.get('/shop-reviews/admin/all', { params });
  },

  // Approve a shop review
  toggleApprove: (reviewId) => {
    return API.patch(`/shop-reviews/admin/${reviewId}/approve`);
  },

  // Toggle highlight status
  toggleHighlight: (reviewId) => {
    return API.patch(`/shop-reviews/admin/${reviewId}/highlight`);
  },

  // Toggle hide status
  toggleHide: (reviewId) => {
    return API.patch(`/shop-reviews/admin/${reviewId}/hide`);
  },

  // Delete a shop review
  deleteReview: (reviewId) => {
    return API.delete(`/shop-reviews/admin/${reviewId}`);
  }
};

export const addressAPI = {
  // Get all addresses for current user
  getAddresses: () => {
    return API.get('/addresses');
  },

  // Get single address by ID
  getAddress: (id) => {
    return API.get(`/addresses/${id}`);
  },

  // Create new address
  createAddress: (addressData) => {
    return API.post('/addresses', addressData);
  },

  // Update address
  updateAddress: (id, addressData) => {
    return API.put(`/addresses/${id}`, addressData);
  },

  // Delete address
  deleteAddress: (id) => {
    return API.delete(`/addresses/${id}`);
  },

  // Set address as default
  setDefaultAddress: (id) => {
    return API.patch(`/addresses/${id}/set-default`);
  }
};

// Chat API endpoints
export const chatAPI = {
  // Get all conversations
  getConversations: () => {
    return API.get('/chat/conversations');
  },

  // Get chat by ID
  getChat: (chatId) => {
    return API.get(`/chat/${chatId}`);
  },

  // Get messages for a chat
  getMessages: (chatId, page = 1, limit = 50) => {
    return API.get(`/chat/${chatId}/messages`, { params: { page, limit } });
  },

  // Create a new chat
  createChat: () => {
    return API.post('/chat/create');
  },

  // Mark messages as read
  markAsRead: (chatId) => {
    return API.post(`/chat/${chatId}/read`);
  }
};

// Favorites API endpoints
export const favoritesAPI = {
  // Get current user's favorites (populated products)
  getFavorites: () => {
    return API.get('/favorites');
  },

  // Toggle favorite for a product
  toggleFavorite: (productId) => {
    return API.post(`/favorites/toggle/${productId}`);
  }
};

export default API; 